/**
 * Tracks whether the Ctrl (Windows/Linux) or Meta/Cmd (Mac) modifier key is currently held down,
 * globally — not tied to any particular element. Extracted out of Canvas.tsx's own inline
 * effect (which used it to show CanvasNode's hand-pinch cursor while hovering a node with the
 * modifier held, signaling a Ctrl+drag connection can be started) so the Plan page's Timeline
 * canvas can drive the exact same cursor affordance on its own cards without duplicating the
 * listener wiring.
 */
import { useEffect, useState } from 'react';

export default function useModifierKeyActive(): boolean {
    const [modifierKeyActive, setModifierKeyActive] = useState(false);

    useEffect(() => {
        const update = (e: KeyboardEvent) => {
            setModifierKeyActive(e.ctrlKey || e.metaKey);
        };

        // Window losing focus (e.g. alt-tabbing away) while the modifier is held would otherwise
        // leave modifierKeyActive stuck true forever — no keyup ever fires for a key released
        // while a DIFFERENT window has focus.
        const clear = () => setModifierKeyActive(false);

        window.addEventListener('keydown', update);
        window.addEventListener('keyup', update);
        window.addEventListener('blur', clear);

        return () => {
            window.removeEventListener('keydown', update);
            window.removeEventListener('keyup', update);
            window.removeEventListener('blur', clear);
        };
    }, []);

    return modifierKeyActive;
}
